import { useRef, useState, useSyncExternalStore, type ReactNode } from 'react'
import { Button } from '@floorfile/ui'
import { Camera, Loader2 } from 'lucide-react'
import type { AttachmentContext } from '@floorfile/contracts'
import { useCopy } from '@floorfile/i18n'
import { useCompanyConfig } from '@/lib/company-config'
import { useUploadAttachment, type AttachmentTarget, type UploadedAttachment } from '../api'
import { attachmentsCopy } from '../copy'
import { isMarkupCandidate, useCaptureMarkup } from './CaptureMarkup'
import { useUploadErrorMessage } from '../upload-errors'

/**
 * F5 — the camera, as a hook, so one capture can be opened from more than one
 * control (a step's camera button AND its "⋯ → Add photo"). It rides the same
 * upload chokepoint as every picker, so a photo taken in a dead zone lands in
 * the device outbox with its `context` and syncs later (F5.2).
 */
export function usePhotoCapture({
  target,
  context,
  onUploaded,
  markup = true,
}: {
  target: AttachmentTarget
  context?: AttachmentContext | undefined
  onUploaded: (attachment: UploadedAttachment) => void
  /** F7: show Mark up / Done after the photo is taken (default on). */
  markup?: boolean | undefined
}): { input: ReactNode; open: () => void; busy: boolean; error: string | undefined; enabled: boolean } {
  const upload = useUploadAttachment()
  const markupFlow = useCaptureMarkup()
  const uploadMessage = useUploadErrorMessage()
  const companyConfig = useCompanyConfig()
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  async function onPick(files: FileList | null) {
    if (!files || files.length === 0) return
    setBusy(true)
    setError(undefined)
    try {
      const picked = Array.from(files)
      for (const file of picked) {
        const uploaded = await upload.mutateAsync({ file, target, ...(context ? { context } : {}) })
        if (markup && picked.length === 1 && isMarkupCandidate(file)) markupFlow.offer({ file, original: uploaded, target, finish: onUploaded })
        else onUploaded(uploaded)
      }
    } catch (err) {
      setError(uploadMessage(err))
    } finally {
      setBusy(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return {
    input: (
      <>
      {markupFlow.dialog}
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept="image/*"
        capture="environment"
        tabIndex={-1}
        aria-hidden
        onChange={(e) => void onPick(e.target.files)}
      />
      </>
    ),
    open: () => inputRef.current?.click(),
    busy,
    error,
    enabled: companyConfig.uploadsEnabled,
  }
}

/** A camera button: opens the rear camera on a phone, the file picker elsewhere. */
export function PhotoCaptureButton({
  target,
  context,
  onUploaded,
  label,
  disabled,
}: {
  target: AttachmentTarget
  context?: AttachmentContext | undefined
  onUploaded: (attachment: UploadedAttachment) => void
  label?: string | undefined
  disabled?: boolean | undefined
}) {
  const c = useCopy(attachmentsCopy).upload
  const capture = usePhotoCapture({ target, context, onUploaded })
  return (
    <span className="inline-flex items-center gap-1.5">
      {capture.input}
      <Button variant="outline" size="sm" className="flex items-center gap-1 text-xs" onClick={capture.open} disabled={disabled || capture.busy || !capture.enabled}>
        {capture.busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Camera className="h-4 w-4" aria-hidden />}
        {capture.enabled ? (label ?? c.takePhoto) : c.disabled}
      </Button>
      {capture.error && <span className="text-xs text-destructive">{capture.error}</span>}
    </span>
  )
}

const coarse = typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia('(pointer: coarse)') : undefined

/** True on a touch-first device — where a "Take photo" button earns its place. */
export function useCoarsePointer(): boolean {
  return useSyncExternalStore(
    (notify) => {
      coarse?.addEventListener('change', notify)
      return () => coarse?.removeEventListener('change', notify)
    },
    () => coarse?.matches ?? false,
    () => false,
  )
}
